import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { css } from '@emotion/css';

import slide1Jpg from './images/slide1/image@2x.jpg';
import slide1Webp from './images/slide1/image@2x.webp';
import slide2Jpg from './images/slide2/image@2x.jpg';
import slide2Webp from './images/slide2/image@2x.webp';
import slide3Jpg from './images/slide3/image@2x.jpg';
import slide3Webp from './images/slide3/image@2x.webp';
import slide4Jpg from './images/slide4/image@2x.jpg';
import slide4Webp from './images/slide4/image@2x.webp';
import slide5Jpg from './images/slide5/image@2x.jpg';
import slide5Webp from './images/slide5/image@2x.webp';
import slide6Jpg from './images/slide6/image@2x.jpg';
import slide6Webp from './images/slide6/image@2x.webp';
import slide7Jpg from './images/slide7/image@2x.jpg';
import slide7Webp from './images/slide7/image@2x.webp';
import slide8Jpg from './images/slide8/image@2x.jpg';
import slide8Webp from './images/slide8/image@2x.webp';
import slide9Jpg from './images/slide9/image@2x.jpg';
import slide9Webp from './images/slide9/image@2x.webp';
import { GRAY1000, GRAY400, INFOGRAPHICS_CHARCOAL20, TEAL200 } from '../../../../../../domain/colors';
import { ImageSource } from '../../../../../common/display/imageSource/ImageSource';
import { Msg, useIntl } from '@/i18n';

const styles = {
  sliderContainer: {
    display: 'flex',
  },
  slide: {
    flex: '0 0 100%',
  },
  img: {
    marginLeft: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    height: 'auto',
    borderTop: `1px solid ${GRAY400}`,
    '@media screen and (min-width: 669px)': {
      width: '70%',
    },
  },
  navigationContainer: {
    width: '70%',
    margin: '0 auto',
  },
  control: {
    '@media screen and (min-width: 669px)': {
      backgroundColor: 'unset',
      zIndex: 1,
      border: 'none',
      color: GRAY1000,
      fontSize: '2.4rem',
    },
  },
  indicatorContainer: {
    '.Indicator + .Indicator': {
      marginLeft: 12,
    },
  },
  indicator: {
    width: 8,
    height: 8,
    borderRadius: 9999,
    border: 'none',
    padding: 0,
    backgroundColor: INFOGRAPHICS_CHARCOAL20,
  },
  indicatorActive: {
    backgroundColor: TEAL200,
  },
} as const;

const slides = [
  {
    image: slide1Jpg,
    imageWebp: slide1Webp,
    title: 'benefits.applicationPackageMieterPlus.modal.overview.slide1',
    defaultTitle: 'Cover sheet',
  },
  {
    image: slide2Jpg,
    imageWebp: slide2Webp,
    title: 'benefits.applicationPackageMieterPlus.modal.overview.slide2',
    defaultTitle: 'Self report (1 of 2)',
  },
  {
    image: slide3Jpg,
    imageWebp: slide3Webp,
    title: 'benefits.applicationPackageMieterPlus.modal.overview.slide3',
    defaultTitle: 'Self report (2 of 2)',
  },
  {
    image: slide4Jpg,
    imageWebp: slide4Webp,
    title: 'benefits.applicationPackageMieterPlus.modal.overview.slide4',
    defaultTitle: 'Schufa-check',
  },
  {
    image: slide5Jpg,
    imageWebp: slide5Webp,
    title: 'benefits.applicationPackageMieterPlus.modal.overview.slide5',
    defaultTitle: 'Schufa-check',
  },
  {
    image: slide6Jpg,
    imageWebp: slide6Webp,
    title: 'benefits.applicationPackageMieterPlus.modal.overview.slide6',
    defaultTitle: 'Schufa-check',
  },
  {
    image: slide7Jpg,
    imageWebp: slide7Webp,
    title: 'benefits.applicationPackageMieterPlus.modal.overview.slide7',
    defaultTitle: 'Schufa-check',
  },
  {
    image: slide8Jpg,
    imageWebp: slide8Webp,
    title: 'benefits.applicationPackageMieterPlus.modal.overview.slide8',
    defaultTitle: 'Schufa-check',
  },
  {
    image: slide9Jpg,
    imageWebp: slide9Webp,
    title: 'benefits.applicationPackageMieterPlus.modal.overview.slide9',
    defaultTitle: 'Schufa-check',
  },
];

export const Carousel = () => {
  const { t } = useIntl();

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);
  const onSelect = useCallback(
    () => emblaApi && setSelectedIndex(emblaApi.selectedScrollSnap()),
    [emblaApi, setSelectedIndex],
  );

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <>
      <div ref={emblaRef} className='overflow-hidden'>
        <div style={styles.sliderContainer}>
          {slides.map((slide, i) => (
            <div key={slide.title} className='absolute-reference' style={styles.slide}>
              <ImageSource
                className={css(styles.img)}
                image={slide.image}
                imageWebp={slide.imageWebp}
                imageAlt={t({ id: slide.title, defaultMessage: slide.defaultTitle })}
                sizes='(min-width: 669px) 371px, 90vw'
              />
            </div>
          ))}
        </div>
      </div>
      <div
        className='grid grid-flex grid-justify-space-between grid-align-end padding-top-s'
        style={styles.navigationContainer}
      >
        <button
          aria-label={t({ id: 'common.buttons.previous', defaultMessage: 'Previous' })}
          className={`palm-hide cursor-pointer grid-item ${css(styles.control)}`}
          onClick={scrollPrev}
        >
          <i className='is24-icon-chevron-left' />
        </button>
        <div className='grid-item palm-one-whole'>
          <div className='align-center palm-padding-bottom-m padding-bottom-s'>
            <Msg id={slides[selectedIndex].title} defaultMessage={slides[selectedIndex].defaultTitle} />
          </div>
          <div className={`grid grid-flex grid-justify-center grid-align-center ${css(styles.indicatorContainer)}`}>
            {slides.map((slide, i) => (
              <button
                aria-label={t({
                  id: 'benefits.applicationPackageMieterPlus.modal.overview.indicator',
                  defaultMessage: 'Indicator',
                })}
                key={slide.title}
                className='Indicator cursor-pointer'
                style={{ ...styles.indicator, ...(i === selectedIndex && styles.indicatorActive) }}
                onClick={() => scrollTo(i)}
              />
            ))}
          </div>
        </div>
        <button
          aria-label={t({ id: 'common.buttons.next', defaultMessage: 'Next' })}
          className={`palm-hide cursor-pointer grid-item ${css(styles.control)}`}
          onClick={scrollNext}
        >
          <i className='is24-icon-chevron-right' />
        </button>
      </div>
    </>
  );
};
